import React, { useState, useEffect } from 'react';
import { FiImage, FiDownload, FiExternalLink, FiUpload, FiX, FiFile, FiTrash2 } from 'react-icons/fi';
import axios from '../config/axios';
import { useTranslation } from '../hooks/useTranslation';

const ImageGallery = () => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadFiles = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/files/list');

      // Use current domain for images (works with any subdomain: cms, test, www)
      const baseUrl = window.location.hostname === 'localhost'
        ? 'http://localhost:3005'
        : `${window.location.protocol}//${window.location.hostname}`;

      const formattedFiles = response.data.files.map(file => ({
        ...file,
        fullUrl: baseUrl + file.url,
        displayName: file.filename.replace(/^\d+_/, ''),
        isPdf: file.type === 'pdf',
        isDesignFile: ['ai', 'psd'].includes(file.type),
        isSvg: file.type === 'svg'
      }));

      setFiles(formattedFiles);
      setLoading(false);
    } catch (err) {
      console.error('Error loading files:', err);
      setError('Failed to load files');
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, []);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'image/svg+xml', // .svg files
      'text/xml', // .svg files (alternative)
      'application/pdf',
      'application/postscript', // .ai files
      'image/vnd.adobe.photoshop', // .psd files
      'application/octet-stream' // .psd files (alternative)
    ];

    // Also check file extension as fallback
    const fileName = file.name.toLowerCase();
    const validExtension = /\.(jpe?g|png|gif|webp|svg|pdf|ai|psd)$/i.test(fileName);

    if (!allowedTypes.includes(file.type) && !validExtension) {
      setError('Only images (JPEG, PNG, GIF, WebP, SVG), PDF, AI, and PSD files are allowed');
      return;
    }

    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      setError('File size must be less than 50MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      setError(null);
      setUploadProgress(0);

      const response = await axios.post('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes timeout for large file uploads
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      setSuccess(`File "${file.name}" uploaded successfully!`);
      setTimeout(() => setSuccess(null), 5000);

      // Reload files list
      await loadFiles();

      // Reset file input
      event.target.value = '';
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      return;
    }

    try {
      await axios.delete(`/files/delete/${filename}`);
      setSuccess('File deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      await loadFiles();
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.response?.data?.error || 'Failed to delete file');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      // Create a synthetic event to pass to handleFileUpload
      const syntheticEvent = {
        target: {
          files: droppedFiles,
          value: ''
        }
      };
      handleFileUpload(syntheticEvent);
    }
  };

  const handleImageClick = (file) => {
    setSelectedFile(file);
  };

  const handleDownload = (file) => {
    window.open(file.fullUrl, '_blank');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('cms.common.loading')}...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-600 font-heading">{t('cms.gallery.title')}</h1>
          <p className="text-gray-600 mt-1">Stock images, marketing materials, and documents</p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <FiImage className="w-4 h-4" />
          <span>{files.length} files</span>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Upload Files</h2>
          <span className="text-sm text-gray-500">Max size: 50MB</span>
        </div>

        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-500 transition-colors"
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id="fileUpload"
            className="hidden"
            accept="image/*,.svg,.pdf,.ai,.psd"
            onChange={handleFileUpload}
            disabled={uploading}
          />
          <label
            htmlFor="fileUpload"
            className="cursor-pointer flex flex-col items-center"
          >
            <FiUpload className="w-12 h-12 text-gray-400 mb-3" />
            <p className="text-gray-700 font-medium">
              {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Images (JPEG, PNG, GIF, WebP, SVG), PDF, AI, and PSD files
            </p>
          </label>

          {uploading && (
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 mt-2">{uploadProgress}% uploaded</p>
            </div>
          )}
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-600 hover:text-green-800">
            <FiX className="w-5 h-5" />
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
            <FiX className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Gallery Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {files.map((file, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer"
            onClick={() => !file.isPdf && !file.isDesignFile && handleImageClick(file)}
          >
            <div className="aspect-video bg-gray-100 relative overflow-hidden">
              {file.isPdf ? (
                <div className="w-full h-full flex items-center justify-center bg-red-50">
                  <FiFile className="w-16 h-16 text-red-500" />
                </div>
              ) : file.isDesignFile ? (
                <div className="w-full h-full flex flex-col items-center justify-center bg-primary-50">
                  <FiFile className="w-16 h-16 text-primary-500 mb-2" />
                  <span className="text-xs font-medium text-primary-600 uppercase">{file.type}</span>
                </div>
              ) : (
                <img
                  src={file.fullUrl}
                  alt={file.displayName}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E';
                  }}
                />
              )}
            </div>
            <div className="p-4">
              <h3 className="font-medium text-gray-900 truncate" title={file.filename}>
                {file.displayName}
              </h3>
              <p className="text-xs text-gray-500 mt-1">{formatFileSize(file.size)}</p>
              <div className="mt-3 flex items-center justify-between">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(file);
                  }}
                  className="flex items-center space-x-1 text-sm text-primary-600 hover:text-primary-800 transition-colors"
                >
                  <FiDownload className="w-4 h-4" />
                  <span>Download</span>
                </button>
                <div className="flex items-center space-x-2">
                  <a
                    href={file.fullUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    <FiExternalLink className="w-4 h-4" />
                  </a>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(file.filename);
                    }}
                    className="flex items-center space-x-1 text-sm text-red-600 hover:text-red-800 transition-colors"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {files.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-500">
          <FiImage className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p>No files uploaded yet. Upload your first file above!</p>
        </div>
      )}

      {/* Image Preview Modal */}
      {selectedFile && !selectedFile.isPdf && !selectedFile.isDesignFile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4"
          onClick={() => setSelectedFile(null)}
        >
          <div className="relative max-w-6xl max-h-[90vh] bg-white rounded-lg overflow-hidden">
            <button
              onClick={() => setSelectedFile(null)}
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
            <img
              src={selectedFile.fullUrl}
              alt={selectedFile.displayName}
              className="max-w-full max-h-[90vh] object-contain"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
              <h3 className="text-white text-xl font-semibold">{selectedFile.displayName}</h3>
              <p className="text-white/80 text-sm mt-1">{formatFileSize(selectedFile.size)}</p>
              <div className="mt-2 flex space-x-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(selectedFile);
                  }}
                  className="flex items-center space-x-2 btn btn-primary transition-colors"
                >
                  <FiDownload className="w-4 h-4" />
                  <span>Download</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGallery;
